/**
 * DebtCaseCrmPanels — Phase 1 CRM additions to the admin case detail:
 *   • OwnerPicker     — dropdown from real users (/admin/debt/team-users)
 *   • CallLogPanel    — structured call log with Q&A + revisions
 *   • PdFormPanel     — versioned PD form (business / individual)
 *   • LenderAppsPanel — per-lender applications with status pills
 *
 * All four are additive: the existing Notes, Documents, Pipeline stay intact.
 */
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button, Pill } from "../common/Primitives";
import api, { formatApiError } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";
import { Phone, ClipboardList, Landmark, Plus, Save, X, History, CheckCircle2, XCircle } from "lucide-react";

/* ------------------------------------------------------------------ */
/* Client picker                                                       */
/* ------------------------------------------------------------------ */
export function ClientPicker({ caseId, currentClientId, caseUid, onChange }) {
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");

  const load = useCallback(() => {
    setLoading(true);
    api.get("/debt/clients", { params: q ? { q, limit: 20 } : { limit: 50 } })
      .then((r) => setClients(r.data?.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [q]);
  useEffect(() => { load(); }, [load]);

  const link = async (client_id) => {
    if (!client_id) return;
    try {
      const r = await api.post(`/debt/clients/${client_id}/link-case`, { case_id: caseId });
      toast.success(`Case linked to client. UID: ${r.data.case_uid || caseUid || "—"}`);
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Client (borrower record)</label>
      <div className="flex gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search by name, PAN, email…"
          className="flex-1 px-3 py-2 border border-navy/15 rounded text-sm" data-testid="debt-client-picker-search" />
        <select value={currentClientId || ""} onChange={(e) => link(e.target.value)} disabled={loading}
          className="px-3 py-2 border border-navy/15 rounded text-sm bg-white" data-testid="debt-client-picker">
          <option value="">— Not linked —</option>
          {clients.map((c) => (
            <option key={c.client_id} value={c.client_id}>
              {c.client_uid || c.client_id} · {c.legal_name}
            </option>
          ))}
        </select>
      </div>
      {caseUid && <div className="text-[10px] font-mono text-emerald-800 mt-1">Case UID: {caseUid}</div>}
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Owner picker                                                        */
/* ------------------------------------------------------------------ */
export function OwnerPicker({ caseId, currentUserId, currentEmail, onChange }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get("/admin/debt/team-users").then((r) => {
      if (!cancelled) setUsers(r.data?.items || []);
    }).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
    return () => { cancelled = true; };
  }, []);

  const save = async (val) => {
    if (!val) return;
    const [uid, email] = val.split("|");
    try {
      await api.patch(`/debt/cases/${caseId}`, {
        assigned_to_user_id: uid || null,
        assigned_to_email: email || null,
      });
      toast.success("Owner assigned");
      onChange?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const value = currentUserId && currentEmail ? `${currentUserId}|${currentEmail}` : "";

  return (
    <div>
      <label className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Assigned owner</label>
      <select value={value} onChange={(e) => save(e.target.value)} disabled={loading}
        className="w-full mt-0.5 px-3 py-2 border border-navy/15 rounded text-sm bg-white"
        data-testid="debt-owner-picker">
        <option value="">— Unassigned —</option>
        {users.map((u) => (
          <option key={u.user_id} value={`${u.user_id}|${u.email}`}>
            {u.name || u.email} · {u.role}
          </option>
        ))}
      </select>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Call log                                                            */
/* ------------------------------------------------------------------ */
const CALL_QUESTIONS = [
  { key: "requirement_confirmed", label: "Loan requirement confirmed?", type: "yesno" },
  { key: "urgency", label: "Timeline / urgency", type: "text" },
  { key: "existing_lenders", label: "Existing lenders / obligations", type: "text" },
  { key: "existing_ca_cs", label: "Has CA/CS engaged?", type: "yesno" },
  { key: "docs_readiness", label: "Docs readiness (P&L, banking, GST)", type: "text" },
  { key: "concerns", label: "Client concerns / objections", type: "text" },
];

export function CallLogPanel({ caseId, onChanged }) {
  const [rows, setRows] = useState([]);
  const [outcomes, setOutcomes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(defaultCall());
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/debt/cases/${caseId}/calls`);
      setRows(r.data?.items || []);
      setOutcomes(r.data?.outcomes || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.outcome) { toast.error("Pick an outcome"); return; }
    setSaving(true);
    try {
      await api.post(`/debt/cases/${caseId}/calls`, {
        ...form,
        duration_seconds: Number(form.duration_seconds) || 0,
        next_followup_at: form.next_followup_at || null,
      });
      toast.success("Call logged");
      setForm(defaultCall()); setOpen(false);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className="border border-navy/10 rounded bg-white/60 p-4" data-testid="debt-call-log-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="overline flex items-center gap-1"><Phone size={11} /> Call log</div>
          <div className="text-[10px] font-mono text-ink-muted">Structured intake with edit history — never overwritten silently.</div>
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1"
            data-testid="debt-call-log-new">
            <Plus size={11} /> Log call
          </button>
        )}
      </div>

      {open && (
        <div className="border border-navy/15 rounded p-3 mb-3 space-y-2 bg-white/70" data-testid="debt-call-log-form">
          <div className="flex items-center justify-between">
            <div className="overline">New call</div>
            <button onClick={() => setOpen(false)}><X size={14} className="text-ink-muted" /></button>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            <select value={form.direction} onChange={(e) => setForm({ ...form, direction: e.target.value })}
              className="px-2 py-1.5 border border-navy/15 rounded text-xs bg-white" data-testid="debt-call-direction">
              <option value="outbound">Outbound</option>
              <option value="inbound">Inbound</option>
            </select>
            <input type="number" min="0" value={form.duration_seconds}
              onChange={(e) => setForm({ ...form, duration_seconds: e.target.value })}
              placeholder="Duration (seconds)"
              className="px-2 py-1.5 border border-navy/15 rounded text-xs" data-testid="debt-call-duration" />
            <select value={form.outcome} onChange={(e) => setForm({ ...form, outcome: e.target.value })}
              className="px-2 py-1.5 border border-navy/15 rounded text-xs bg-white" data-testid="debt-call-outcome">
              <option value="">— Outcome —</option>
              {outcomes.map((o) => <option key={o}>{o}</option>)}
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {CALL_QUESTIONS.map((q) => (
              <div key={q.key}>
                <label className="text-[10px] font-mono text-ink-muted">{q.label}</label>
                {q.type === "yesno" ? (
                  <select
                    value={form.questions[q.key] || ""}
                    onChange={(e) => setForm({ ...form, questions: { ...form.questions, [q.key]: e.target.value } })}
                    className="w-full px-2 py-1.5 border border-navy/15 rounded text-xs bg-white"
                    data-testid={`debt-call-q-${q.key}`}
                  >
                    <option value="">—</option>
                    <option>Yes</option>
                    <option>No</option>
                    <option>Unclear</option>
                  </select>
                ) : (
                  <input
                    value={form.questions[q.key] || ""}
                    onChange={(e) => setForm({ ...form, questions: { ...form.questions, [q.key]: e.target.value } })}
                    className="w-full px-2 py-1.5 border border-navy/15 rounded text-xs"
                    data-testid={`debt-call-q-${q.key}`}
                  />
                )}
              </div>
            ))}
          </div>
          <div>
            <label className="text-[10px] font-mono text-ink-muted">Requirement summary</label>
            <input value={form.requirement_summary} onChange={(e) => setForm({ ...form, requirement_summary: e.target.value })}
              placeholder='e.g. "₹2.5 Cr LAP against Bengaluru property, 84m tenure"'
              className="w-full px-2 py-1.5 border border-navy/15 rounded text-xs" data-testid="debt-call-requirement" />
          </div>
          <div>
            <label className="text-[10px] font-mono text-ink-muted">Open remarks</label>
            <textarea rows={2} value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })}
              className="w-full px-2 py-1.5 border border-navy/15 rounded text-xs" data-testid="debt-call-remarks" />
          </div>
          <div>
            <label className="text-[10px] font-mono text-ink-muted">Next follow-up</label>
            <input type="datetime-local" value={form.next_followup_at} onChange={(e) => setForm({ ...form, next_followup_at: e.target.value })}
              className="px-2 py-1.5 border border-navy/15 rounded text-xs" data-testid="debt-call-followup" />
          </div>
          <Button variant="gold" onClick={save} disabled={saving} testId="debt-call-log-save">
            {saving ? "Saving…" : "Save call log"}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="text-xs italic text-ink-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs italic text-ink-muted">No calls logged yet.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((c) => (
            <div key={c.call_id} className="border border-navy/[0.06] rounded p-2 text-xs" data-testid={`debt-call-${c.call_id}`}>
              <div className="flex items-center justify-between flex-wrap gap-1">
                <div className="text-[10px] font-mono text-ink-muted">
                  {c.direction.toUpperCase()} · {c.author_email} · {formatDateTime(c.at)} · {Math.floor((c.duration_seconds || 0)/60)}m {Math.max(0, (c.duration_seconds||0)%60)}s
                </div>
                <Pill tone={c.connected ? "success" : "muted"}>{c.outcome}</Pill>
              </div>
              {c.requirement_summary && <div className="mt-1 text-ink"><b>Req:</b> {c.requirement_summary}</div>}
              {Object.keys(c.questions || {}).length > 0 && (
                <div className="mt-1 grid grid-cols-1 md:grid-cols-2 gap-x-3 text-[11px] text-ink-soft">
                  {Object.entries(c.questions).filter(([, v]) => v).map(([k, v]) => (
                    <div key={k}><b className="text-ink-muted">{k}:</b> {v}</div>
                  ))}
                </div>
              )}
              {c.remarks && <div className="mt-1 whitespace-pre-wrap italic text-ink-soft">&ldquo;{c.remarks}&rdquo;</div>}
              {c.next_followup_at && (
                <div className="mt-1 text-[10px] font-mono text-navy">Follow-up: {formatDateTime(c.next_followup_at)}</div>
              )}
              {c.revisions && c.revisions.length > 0 && (
                <div className="mt-1 text-[10px] font-mono text-amber-800 inline-flex items-center gap-1">
                  <History size={10} /> {c.revisions.length} edit{c.revisions.length === 1 ? "" : "s"}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
function defaultCall() {
  return { direction: "outbound", duration_seconds: "", outcome: "", requirement_summary: "", next_followup_at: "", connected: true, questions: {}, remarks: "" };
}

/* ------------------------------------------------------------------ */
/* PD Form                                                             */
/* ------------------------------------------------------------------ */
const BUSINESS_PD_FIELDS = [
  ["legal_name", "Legal name"],
  ["constitution", "Constitution (Pvt Ltd / LLP / Prop.)"],
  ["incorporation_date", "Incorporation date"],
  ["industry", "Industry"],
  ["business_model", "Business model"],
  ["promoters", "Promoters"],
  ["shareholding", "Shareholding %"],
  ["vintage_years", "Business vintage (years)"],
  ["turnover_inr", "Turnover (₹)"],
  ["ebitda_inr", "EBITDA (₹)"],
  ["pat_inr", "PAT (₹)"],
  ["net_worth_inr", "Net worth (₹)"],
  ["existing_borrowings_inr", "Existing borrowings (₹)"],
  ["existing_lenders", "Existing lenders"],
  ["banking", "Banking partners"],
  ["gst_turnover_inr", "GST turnover (₹)"],
  ["collateral", "Collateral"],
  ["loan_requirement_inr", "Loan requirement (₹)"],
  ["end_use", "End use"],
  ["tenure_months", "Tenure (months)"],
  ["expected_pricing", "Expected pricing"],
  ["previous_rejections", "Previous lender approaches / rejections"],
  ["strengths", "Key strengths"],
  ["concerns", "Key concerns"],
];
const INDIVIDUAL_PD_FIELDS = [
  ["employment", "Employment / business"],
  ["annual_income_inr", "Annual income (₹)"],
  ["existing_obligations_inr", "Existing obligations (₹)"],
  ["current_emi_inr", "Current EMI (₹)"],
  ["assets_inr", "Assets (₹)"],
  ["liabilities_inr", "Liabilities (₹)"],
  ["loan_requirement_inr", "Loan requirement (₹)"],
  ["purpose", "Purpose"],
  ["tenure_months", "Tenure (months)"],
  ["security", "Security"],
  ["credit_history", "Credit history / bureau"],
  ["banking", "Banking information"],
];

export function PdFormPanel({ caseId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [template, setTemplate] = useState("business");
  const [form, setForm] = useState({});
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/debt/cases/${caseId}/pd`);
      setRows(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      await api.post(`/debt/cases/${caseId}/pd`, { template, data: form });
      toast.success("PD form saved");
      setForm({}); setOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const fields = template === "business" ? BUSINESS_PD_FIELDS : INDIVIDUAL_PD_FIELDS;

  return (
    <div className="border border-navy/10 rounded bg-white/60 p-4" data-testid="debt-pd-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="overline flex items-center gap-1"><ClipboardList size={11} /> Personal Discussion (PD)</div>
          <div className="text-[10px] font-mono text-ink-muted">Bank-style structured PD. Versioned — old PDs are never overwritten.</div>
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1"
            data-testid="debt-pd-new">
            <Plus size={11} /> New PD
          </button>
        )}
      </div>

      {open && (
        <div className="border border-navy/15 rounded p-3 mb-3 bg-white/70 space-y-2" data-testid="debt-pd-form">
          <div className="flex items-center justify-between">
            <div className="overline">New PD form</div>
            <button onClick={() => setOpen(false)}><X size={14} className="text-ink-muted" /></button>
          </div>
          <div>
            <label className="text-[10px] font-mono text-ink-muted">Template</label>
            <select value={template} onChange={(e) => { setTemplate(e.target.value); setForm({}); }}
              className="w-full px-2 py-1.5 border border-navy/15 rounded text-xs bg-white" data-testid="debt-pd-template">
              <option value="business">Business borrower</option>
              <option value="individual">Individual borrower</option>
            </select>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-[400px] overflow-y-auto pr-1">
            {fields.map(([k, label]) => (
              <div key={k}>
                <label className="text-[10px] font-mono text-ink-muted">{label}</label>
                <input value={form[k] || ""} onChange={(e) => setForm({ ...form, [k]: e.target.value })}
                  className="w-full px-2 py-1.5 border border-navy/15 rounded text-xs" data-testid={`debt-pd-${k}`} />
              </div>
            ))}
          </div>
          <Button variant="gold" onClick={save} disabled={saving} testId="debt-pd-save">
            <Save size={12} className="inline mr-1" />
            {saving ? "Saving…" : "Save PD (new version)"}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="text-xs italic text-ink-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs italic text-ink-muted">No PD recorded yet.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((p) => (
            <details key={p.pd_id} className="border border-navy/[0.06] rounded p-2 text-xs" data-testid={`debt-pd-${p.pd_id}`}>
              <summary className="cursor-pointer flex items-center justify-between">
                <span className="font-heading text-ink">v{p.version} · {p.template} · {p.conducted_by_email}</span>
                <span className="text-[10px] font-mono text-ink-muted">{formatDateTime(p.recorded_at)}</span>
              </summary>
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-3 gap-y-1">
                {Object.entries(p.data || {}).filter(([, v]) => v !== "" && v != null).map(([k, v]) => (
                  <div key={k}><b className="text-ink-muted">{k}:</b> <span className="text-ink">{String(v)}</span></div>
                ))}
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Lender applications                                                 */
/* ------------------------------------------------------------------ */
const APP_STAGES = ["Mapped", "Documents Prepared", "Submitted", "Login Confirmed", "Under Credit", "Query Raised", "Query Responded", "Approved", "Sanctioned", "Rejected", "Withdrawn"];
const SANCTIONED_STAGES = new Set(["Approved", "Sanctioned"]);

export function LenderAppsPanel({ caseId, onChanged }) {
  const [rows, setRows] = useState([]);
  const [lenders, setLenders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ lender_id: "", amount_requested_inr: "", product_type: "term_loan" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/debt/cases/${caseId}/lender-applications`);
      setRows(r.data?.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [caseId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    api.get("/admin/lenders", { params: { active_only: true } })
      .then((r) => setLenders(r.data?.items || []))
      .catch(() => {});
  }, []);

  const create = async () => {
    if (!form.lender_id || !form.amount_requested_inr) { toast.error("Pick a lender and amount"); return; }
    setSaving(true);
    try {
      await api.post(`/debt/cases/${caseId}/lender-applications`, {
        ...form,
        amount_requested_inr: Number(form.amount_requested_inr),
      });
      toast.success("Lender application created");
      setForm({ lender_id: "", amount_requested_inr: "", product_type: "term_loan" });
      setOpen(false);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  const patch = async (appId, changes) => {
    try {
      await api.patch(`/debt/lender-applications/${appId}`, changes);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="border border-navy/10 rounded bg-white/60 p-4" data-testid="debt-lender-apps-panel">
      <div className="flex items-center justify-between mb-2">
        <div>
          <div className="overline flex items-center gap-1"><Landmark size={11} /> Lender applications</div>
          <div className="text-[10px] font-mono text-ink-muted">Track every lender submission — status pill per row so nothing falls through.</div>
        </div>
        {!open && (
          <button onClick={() => setOpen(true)} className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1"
            data-testid="debt-lender-app-new">
            <Plus size={11} /> Add lender
          </button>
        )}
      </div>

      {open && (
        <div className="border border-navy/15 rounded p-3 mb-3 bg-white/70 space-y-2" data-testid="debt-lender-app-form">
          <div className="flex items-center justify-between">
            <div className="overline">New lender application</div>
            <button onClick={() => setOpen(false)}><X size={14} className="text-ink-muted" /></button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <select value={form.lender_id} onChange={(e) => setForm({ ...form, lender_id: e.target.value })}
              className="px-2 py-1.5 border border-navy/15 rounded text-xs bg-white" data-testid="debt-lender-app-lender">
              <option value="">— Lender —</option>
              {lenders.map((l) => <option key={l.lender_id} value={l.lender_id}>{l.name} · {l.lender_type}</option>)}
            </select>
            <input type="number" value={form.amount_requested_inr} onChange={(e) => setForm({ ...form, amount_requested_inr: e.target.value })}
              placeholder="Amount requested (INR)" className="px-2 py-1.5 border border-navy/15 rounded text-xs"
              data-testid="debt-lender-app-amount" />
            <select value={form.product_type} onChange={(e) => setForm({ ...form, product_type: e.target.value })}
              className="px-2 py-1.5 border border-navy/15 rounded text-xs bg-white" data-testid="debt-lender-app-product">
              {["term_loan","working_capital","invoice_discounting","lap","home_loan","business_loan","personal_loan","equipment_finance","other"].map((p) => <option key={p}>{p}</option>)}
            </select>
          </div>
          <Button variant="gold" onClick={create} disabled={saving} testId="debt-lender-app-save">
            {saving ? "Creating…" : "Create application"}
          </Button>
        </div>
      )}

      {loading ? (
        <div className="text-xs italic text-ink-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs italic text-ink-muted">No lender applications yet.</div>
      ) : (
        <div className="space-y-2">
          {rows.map((a) => (
            <div key={a.lender_app_id} className="border border-navy/[0.06] rounded p-2 text-xs" data-testid={`debt-lender-app-${a.lender_app_id}`}>
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <div className="font-heading text-ink">{a.lender_name} <span className="text-[10px] font-mono text-ink-muted">· {a.lender_app_uid}</span></div>
                  <div className="text-[10px] font-mono text-ink-muted">
                    Req ₹{Number(a.amount_requested_inr || 0).toLocaleString("en-IN")} · {a.product_type || "—"} · {formatDateTime(a.created_at)}
                  </div>
                </div>
                <select value={a.stage} onChange={(e) => patch(a.lender_app_id, { stage: e.target.value })}
                  className="px-2 py-1 border border-navy/15 rounded text-[11px] bg-white" data-testid={`debt-lender-app-stage-${a.lender_app_id}`}>
                  {APP_STAGES.map((s) => <option key={s}>{s}</option>)}
                </select>
              </div>
              {SANCTIONED_STAGES.has(a.stage) && (
                <div className="mt-1 grid grid-cols-2 md:grid-cols-4 gap-2">
                  <FieldNum label="Sanction (₹)" value={a.sanction_amount_inr} onSave={(v) => patch(a.lender_app_id, { sanction_amount_inr: v })} testId={`sanction-${a.lender_app_id}`} />
                  <FieldNum label="ROI %" value={a.interest_rate_pa} onSave={(v) => patch(a.lender_app_id, { interest_rate_pa: v })} testId={`roi-${a.lender_app_id}`} />
                  <FieldNum label="Tenure (m)" value={a.tenure_months} onSave={(v) => patch(a.lender_app_id, { tenure_months: v })} testId={`tenure-${a.lender_app_id}`} />
                  <FieldNum label="PF (₹)" value={a.processing_fee_inr} onSave={(v) => patch(a.lender_app_id, { processing_fee_inr: v })} testId={`pf-${a.lender_app_id}`} />
                </div>
              )}
              {a.stage === "Rejected" && (
                <div className="mt-1">
                  <FieldText label="Rejection reason" value={a.rejection_reason} onSave={(v) => patch(a.lender_app_id, { rejection_reason: v })} testId={`rej-${a.lender_app_id}`} />
                </div>
              )}
              {a.history && a.history.length > 1 && (
                <div className="mt-1 text-[10px] font-mono text-ink-muted">
                  {a.history.length} status changes · latest by {a.history[a.history.length-1]?.by}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function FieldNum({ label, value, onSave, testId }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => setV(value ?? ""), [value]);
  return (
    <div>
      <label className="text-[10px] font-mono text-ink-muted">{label}</label>
      <input type="number" value={v} onChange={(e) => setV(e.target.value)}
        onBlur={() => v !== "" && Number(v) !== Number(value) && onSave(Number(v))}
        className="w-full px-2 py-1 border border-navy/15 rounded text-xs"
        data-testid={testId} />
    </div>
  );
}
function FieldText({ label, value, onSave, testId }) {
  const [v, setV] = useState(value ?? "");
  useEffect(() => setV(value ?? ""), [value]);
  return (
    <div>
      <label className="text-[10px] font-mono text-ink-muted">{label}</label>
      <input value={v} onChange={(e) => setV(e.target.value)}
        onBlur={() => v !== value && onSave(v)}
        className="w-full px-2 py-1 border border-navy/15 rounded text-xs"
        data-testid={testId} />
    </div>
  );
}
